/**
 * AdminWeeklyThemes, programa séries temáticas semanais (Domingo a Sábado).
 * Lista todos os temas + formulário para criar (catálogo ou personalizado).
 * Edita active/desativa, deleta.
 */
import React, { useEffect, useState } from "react";
import { Calendar, Plus, Trash2, Power, PowerOff } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = [
  "Alegria", "Aliança", "Amor", "Armas Espirituais", "Arrependimento",
  "Criação", "Cruz", "Família", "Fé", "Graça", "Humildade", "Liderança",
  "Oração", "Perdão", "Salmos",
];

function nextSundayISO(refIso) {
  const d = refIso ? new Date(refIso + "T00:00:00") : new Date();
  const dow = d.getDay(); // 0=Sun
  const add = dow === 0 ? 0 : 7 - dow;
  d.setDate(d.getDate() + add);
  return d.toISOString().slice(0, 10);
}

export default function AdminWeeklyThemes() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState("catalog"); // catalog | custom
  const [form, setForm] = useState({
    week_start: nextSundayISO(),
    theme_slug: "familia",
    theme_name: "",
    theme_description: "",
    anchor_category: "Família",
    accent_color: "#C5A059",
    verse_reference: "",
    verse_text: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: cat }] = await Promise.all([
        api.get("/admin/weekly-themes"),
        api.get("/weekly-theme/catalog"),
      ]);
      setItems(list.items || []);
      setCatalog(cat.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao carregar temas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const payload =
        mode === "catalog"
          ? { week_start: form.week_start, theme_slug: form.theme_slug }
          : {
              week_start: form.week_start,
              theme_name: form.theme_name,
              theme_description: form.theme_description,
              anchor_category: form.anchor_category,
              accent_color: form.accent_color,
              verse_reference: form.verse_reference,
              verse_text: form.verse_text,
            };
      await api.post("/admin/weekly-themes", payload);
      setForm((f) => ({ ...f, week_start: nextSundayISO() }));
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Falha ao criar tema");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/admin/weekly-themes/${it.id}`, { active: !it.active });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao atualizar");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Excluir "${it.theme_name}" (${it.week_start})?`)) return;
    try {
      await api.delete(`/admin/weekly-themes/${it.id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao excluir");
    }
  };

  return (
    <section
      data-testid="admin-weekly-themes-section"
      className="bg-white rounded-3xl shadow-sm border border-[#0B1E36]/8 p-6 md:p-8 mt-8"
    >
      <header className="flex items-center gap-3 mb-5">
        <Calendar className="w-5 h-5 text-[#C5A059]" />
        <div>
          <p className="text-[0.7rem] uppercase tracking-[0.18em] text-[#0B1E36]/55">
            Devocional, séries temáticas
          </p>
          <h2 className="font-serif-display text-2xl text-[#0B1E36]">
            Semanas temáticas (Dom → Sáb)
          </h2>
        </div>
      </header>

      {err && (
        <div
          data-testid="admin-weekly-themes-error"
          className="mb-4 px-3 py-2 rounded bg-rose-50 border border-rose-200 text-rose-700 text-sm"
        >
          {err}
        </div>
      )}

      {/* Form */}
      <form
        onSubmit={submit}
        data-testid="admin-weekly-themes-form"
        className="grid md:grid-cols-12 gap-4 mb-6 p-4 rounded-2xl bg-[#FAF8F4] border border-[#0B1E36]/10"
      >
        <div className="md:col-span-3">
          <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
            Domingo de início
          </label>
          <input
            type="date"
            data-testid="weekly-theme-week-start"
            value={form.week_start}
            onChange={(e) => setForm({ ...form, week_start: e.target.value })}
            className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm"
            required
          />
        </div>

        <div className="md:col-span-3">
          <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
            Modo
          </label>
          <div className="flex gap-2 text-sm">
            <button
              type="button"
              data-testid="weekly-theme-mode-catalog"
              onClick={() => setMode("catalog")}
              className={`px-3 py-2 rounded border flex-1 ${
                mode === "catalog"
                  ? "bg-[#0B1E36] text-white border-[#0B1E36]"
                  : "border-[#0B1E36]/20 text-[#0B1E36]"
              }`}
            >
              Catálogo
            </button>
            <button
              type="button"
              data-testid="weekly-theme-mode-custom"
              onClick={() => setMode("custom")}
              className={`px-3 py-2 rounded border flex-1 ${
                mode === "custom"
                  ? "bg-[#0B1E36] text-white border-[#0B1E36]"
                  : "border-[#0B1E36]/20 text-[#0B1E36]"
              }`}
            >
              Personalizado
            </button>
          </div>
        </div>

        {mode === "catalog" ? (
          <div className="md:col-span-6">
            <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
              Tema do catálogo
            </label>
            <select
              data-testid="weekly-theme-catalog-select"
              value={form.theme_slug}
              onChange={(e) => setForm({ ...form, theme_slug: e.target.value })}
              className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm bg-white"
            >
              {catalog.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name} ({c.anchor_category})
                </option>
              ))}
            </select>
          </div>
        ) : (
          <>
            <div className="md:col-span-6">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Nome do tema
              </label>
              <input
                type="text"
                data-testid="weekly-theme-custom-name"
                value={form.theme_name}
                onChange={(e) => setForm({ ...form, theme_name: e.target.value })}
                placeholder="Ex.: Semana da Restauração"
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm"
                required
              />
            </div>
            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Categoria-âncora
              </label>
              <select
                data-testid="weekly-theme-custom-category"
                value={form.anchor_category}
                onChange={(e) => setForm({ ...form, anchor_category: e.target.value })}
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm bg-white"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Cor
              </label>
              <input
                type="color"
                data-testid="weekly-theme-custom-color"
                value={form.accent_color}
                onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                className="w-full h-10 rounded border border-[#0B1E36]/15"
              />
            </div>
            <div className="md:col-span-12">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Descrição (opcional)
              </label>
              <input
                type="text"
                data-testid="weekly-theme-custom-description"
                value={form.theme_description}
                onChange={(e) => setForm({ ...form, theme_description: e.target.value })}
                placeholder="Sete dias contemplando..."
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm"
              />
            </div>
            <div className="md:col-span-6">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Versículo (referência)
              </label>
              <input
                type="text"
                data-testid="weekly-theme-custom-verse-ref"
                value={form.verse_reference}
                onChange={(e) => setForm({ ...form, verse_reference: e.target.value })}
                placeholder="Ex.: Romanos 12:2"
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm"
              />
            </div>
            <div className="md:col-span-6">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Versículo (texto)
              </label>
              <input
                type="text"
                data-testid="weekly-theme-custom-verse-text"
                value={form.verse_text}
                onChange={(e) => setForm({ ...form, verse_text: e.target.value })}
                placeholder="Texto curto do versículo"
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm"
              />
            </div>
          </>
        )}

        <div className="md:col-span-12 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="weekly-theme-submit"
            className="px-5 py-2.5 rounded bg-[#C5A059] hover:bg-[#a88848] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Plus className="w-4 h-4" />
            {saving ? "Salvando..." : "Programar semana"}
          </button>
        </div>
      </form>

      {/* List */}
      <div data-testid="admin-weekly-themes-list">
        {loading ? (
          <p className="text-sm text-[#0B1E36]/55">Carregando...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#0B1E36]/55">
            Nenhuma semana temática programada ainda. Use o formulário acima.
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`weekly-theme-row-${it.id}`}
                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[#0B1E36]/10 bg-white"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: it.accent_color }}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[#0B1E36] truncate">
                        {it.theme_name}
                      </p>
                      {!it.active && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#0B1E36]/10 text-[#0B1E36]/65">
                          Inativo
                        </span>
                      )}
                      {it.is_custom && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#C5A059]/15 text-[#8B6A2F]">
                          Custom
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#0B1E36]/55">
                      {it.week_start} → {it.week_end} · âncora: {it.anchor_category}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleActive(it)}
                    data-testid={`weekly-theme-toggle-${it.id}`}
                    className="p-2 rounded hover:bg-[#0B1E36]/5 text-[#0B1E36]/65"
                    title={it.active ? "Desativar" : "Ativar"}
                  >
                    {it.active ? (
                      <Power className="w-4 h-4" />
                    ) : (
                      <PowerOff className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(it)}
                    data-testid={`weekly-theme-delete-${it.id}`}
                    className="p-2 rounded hover:bg-rose-50 text-rose-600"
                    title="Excluir"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
